
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Dialog } from "@/components/ui/dialog";
import { PricingSheet } from "@/components/pricing/PricingSheet";
import { useAuth } from "@/contexts/AuthContext";
import { useChatState } from "@/hooks/useChatState";
import { ChatAuthStateWrapper } from "./ChatAuthStateWrapper";
import { ChatMessageArea } from "./ChatMessageArea";
import { ChatInputArea } from "./ChatInputArea";
import { LoadingSpinner3D } from "@/components/LoadingSpinner3D";
import { useSearchParams } from "react-router-dom";
import PaymentSuccessMessage from "./PaymentSuccessMessage";
import { ReportLimitDialog } from "@/components/chat/ReportLimitDialog";
import { Button } from "@/components/ui/button";
import { LogOut } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";

/**
 * CountdownTimer component that displays a countdown from 3 minutes
 */
const CountdownTimer = () => {
  const [timeLeft, setTimeLeft] = useState(180); // 3 minutes in seconds

  useEffect(() => {
    // Don't start the timer if we're at 0
    if (timeLeft <= 0) return;

    // Set up the interval
    const intervalId = setInterval(() => {
      setTimeLeft((prevTime) => prevTime - 1);
    }, 1000);

    // Clear the interval when component unmounts or when timeLeft reaches 0
    return () => clearInterval(intervalId);
  }, [timeLeft]);

  // Format the time as mm:ss
  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;
  const formattedTime = `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;

  return (
    <div className="text-blue-400 font-medium text-center">
      <p>Estimated time remaining</p>
      <p className="text-2xl font-bold">{formattedTime}</p>
    </div>
  );
};

/**
 * ChatContainer - Main component for the chat interface
 * Manages authentication state, message display, and user input
 */
export function ChatContainer() {
  const { isAuthenticated, isLoading: authLoading, signOut, user } = useAuth();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const navigate = useNavigate();
  const {
    messages,
    isLoading,
    isTransitioning,
    showPricing,
    setShowPricing,
    fadingIn,
    isLoadingChatHistory,
    initializeChatHistory,
    handleSendMessage,
  } = useChatState();

  // Ref to track if initialization has happened
  const initialized = useRef(false);
  const currentChatRef = useRef<string | null>(null);

  // Run initialization only once when authentication state is determined
  useEffect(() => {
    if (isAuthenticated !== null && !initialized.current) {
      const chatId = searchParams.get("chat");
      initializeChatHistory(isAuthenticated, chatId);
      initialized.current = true;
      currentChatRef.current = chatId;
    }
  }, [isAuthenticated, initializeChatHistory, searchParams]);

  // Reset initialization when chat ID changes in URL
  useEffect(() => {
    const chatId = searchParams.get("chat");
    if (initialized.current && isAuthenticated && chatId !== currentChatRef.current) {
      console.log("Chat ID changed in URL, reinitializing", chatId);
      initializeChatHistory(isAuthenticated, chatId);
      currentChatRef.current = chatId;
    }
  }, [searchParams, isAuthenticated, initializeChatHistory]);

  const handleSignOut = async () => {
    try {
      await signOut();
      // Navigation is handled in the AuthContext
      toast({
        title: "Signed out successfully",
        description: "You have been signed out of your account.",
      });
    } catch (error) {
      console.error("Error during sign out:", error);
      toast({
        title: "Error signing out",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  // Display loading spinner while authentication is being determined
  if (authLoading || isAuthenticated === null) {
    return <ChatAuthStateWrapper />;
  }

  return (
    <div className="flex flex-col h-full bg-black text-white overflow-hidden">
      {/* Logout button - Added to the top right corner */}
      {isAuthenticated && (
        <div className="absolute top-4 right-4 z-20">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSignOut}
            className="text-red-400 hover:text-red-300 hover:bg-red-900/20 flex items-center gap-2"
          >
            <LogOut className="h-4 w-4" />
            <span>Sign Out</span>
          </Button>
        </div>
      )}
      
      {/* ReportLimitDialog - Show at the top of the component */}
      <ReportLimitDialog />

      {/* Payment Success Message - Only shows after successful payment */}
      {isAuthenticated && <PaymentSuccessMessage />}

      <div className="max-w-3xl mx-auto h-full pt-4 pb-32">
        <ChatMessageArea
          messages={messages}
          isTransitioning={isTransitioning}
          fadingIn={fadingIn}
        />
      </div>

      {/* Loading overlay - Show when loading or when loading chat history */}
      {(isLoading || isLoadingChatHistory) && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 backdrop-blur-sm z-10"
        >
          {isLoadingChatHistory ? (
            <>
              <LoadingSpinner3D />
              <p className="text-white mt-4">Loading chat history...</p>
            </>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <CountdownTimer />
              <LoadingSpinner3D />
            </div>
          )}
        </motion.div>
      )}

      <ChatInputArea
        handleSendMessage={handleSendMessage}
        isLoading={isLoading || isLoadingChatHistory}
      />

      {/* Updated Dialog component with medium size */}
      <Dialog open={showPricing} onOpenChange={setShowPricing}>
        <PricingSheet onClose={() => setShowPricing(false)} />
      </Dialog>
    </div>
  );
}
